import React, { useState, useEffect, useRef, useMemo, useImperativeHandle } from 'react';
import { Dice1, Dice2, Dice3, Dice4, Dice5, Dice6, RefreshCw, ChevronDown } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from './components/ui/alert';
import { useLanguage } from './contexts/LanguageContext';
import { isIPadSafari } from './utils/deviceDetection';

// Define language settings object, mapping scene to corresponding language code
const languageSettings = {
  // English exam types (keep original English exams unchanged)
  'IELTS': { code: 'en-US', name: 'English' },
  'TOEFL': { code: 'en-US', name: 'English' },
  'CET4': { code: 'en-US', name: 'English' },
  'CET6': { code: 'en-US', name: 'English' },
  'CPE': { code: 'en-US', name: 'English' },
  'CAE': { code: 'en-US', name: 'English' },
  
  // French exam types (4 authoritative exams)
  'DELF': { code: 'fr-FR', name: 'French' },
  'DALF': { code: 'fr-FR', name: 'French' },
  'TCF': { code: 'fr-FR', name: 'French' },
  'TEF': { code: 'fr-FR', name: 'French' },
  
  // Spanish exam types (4 authoritative exams)
  'DELE': { code: 'es-ES', name: 'Spanish' },
  'SIELE': { code: 'es-ES', name: 'Spanish' },
  'CELU': { code: 'es-ES', name: 'Spanish' },
  'DIE': { code: 'es-ES', name: 'Spanish' },
  
  // Japanese exam types (4 authoritative exams)
  'JLPT': { code: 'ja-JP', name: 'Japanese' },
  'JPT': { code: 'ja-JP', name: 'Japanese' },
  'J.TEST': { code: 'ja-JP', name: 'Japanese' },
  'NAT-TEST': { code: 'ja-JP', name: 'Japanese' },
  
  // Korean exam types (4 authoritative exams)
  'TOPIK': { code: 'ko-KR', name: 'Korean' },
  'KLPT': { code: 'ko-KR', name: 'Korean' },
  'FLEX': { code: 'ko-KR', name: 'Korean' },
  'EPS-TOPIK': { code: 'ko-KR', name: 'Korean' },
  
  // Portuguese exam types (4 authoritative exams)
  'CAPLE': { code: 'pt-PT', name: 'Portuguese' },
  'CELPE-Bras': { code: 'pt-PT', name: 'Portuguese' },
  'PLE': { code: 'pt-PT', name: 'Portuguese' },
  'DEPLE': { code: 'pt-PT', name: 'Portuguese' },
  
  // German exam types (4 authoritative exams)
  'TestDaF': { code: 'de-DE', name: 'German' },
  'Goethe': { code: 'de-DE', name: 'German' },
  'DSH': { code: 'de-DE', name: 'German' },
  'telc': { code: 'de-DE', name: 'German' },
  
  // Italian exam types (4 authoritative exams)
  'CELI': { code: 'it-IT', name: 'Italian' },
  'CILS': { code: 'it-IT', name: 'Italian' },
  'PLIDA': { code: 'it-IT', name: 'Italian' },
  'IT': { code: 'it-IT', name: 'Italian' },
  
  // Russian exam types (4 authoritative exams)
  'TORFL': { code: 'ru-RU', name: 'Russian' },
  'TRKI': { code: 'ru-RU', name: 'Russian' },
  'TELC Russian': { code: 'ru-RU', name: 'Russian' },
  'RCT': { code: 'ru-RU', name: 'Russian' },
  
  // Arabic exam types (4 authoritative exams)
  'ALPT': { code: 'ar-SA', name: 'Arabic' },
  'AAAT': { code: 'ar-SA', name: 'Arabic' },
  'ACTFL-OPI': { code: 'ar-SA', name: 'Arabic' },
  'IB Arabic': { code: 'ar-SA', name: 'Arabic' },
  
  // Dutch exam types (4 authoritative exams)
  'CNaVT': { code: 'nl-NL', name: 'Dutch' },
  'NT2': { code: 'nl-NL', name: 'Dutch' },
  'CNAVT': { code: 'nl-NL', name: 'Dutch' },
  'Staatsexamen': { code: 'nl-NL', name: 'Dutch' }
};

// 定义界面文本翻译
const translations = {
  english: {
    rollDice: "Roll Dice",
    prepPhase: "Preparation Phase",
    practicePhase: "Practice Phase",
    prepTimeLeft: "Preparation time left",
    practiceTimeLeft: "Practice time left",
    seconds: "seconds",
    startRecording: "Start Recording",
    stopRecording: "Stop Recording",
    bombTask: "Special Task!",
    topic: "Topic",
    resetGame: "Reset Game",
    gameRules: "Game Rules",
    closeRules: "Close Rules",
    sceneSelectorTitle: "Choose a Learning Scene",
    ielts_improvement_report: "IELTS Speaking Improvement Report",
    feature_optimizing: "Feature Optimizing",
    report_optimization_message: "We are collecting your speaking data to provide you with personalized scoring suggestions. Thank you for your support and understanding!",
    recording_voice: "Recording Voice"
  },
  chinese: {
    rollDice: "摇步数",
    prepPhase: "准备阶段",
    practicePhase: "练习阶段",
    prepTimeLeft: "准备时间剩余",
    practiceTimeLeft: "练习时间剩余",
    seconds: "秒",
    startRecording: "开始录音",
    stopRecording: "停止录音",
    bombTask: "特殊任务！",
    topic: "话题",
    resetGame: "重置游戏",
    gameRules: "游戏规则",
    closeRules: "关闭规则",
    sceneSelectorTitle: "选择学习场景",
    ielts_improvement_report: "雅思口语提分报告",
    feature_optimizing: "功能优化中",
    report_optimization_message: "我们正在收集您的口语数据，以便为您提供更加个性化的提分建议。感谢您的支持和理解！",
    recording_voice: "录制语音"
  },
  japanese: {
    rollDice: "サイコロを振る",
    prepPhase: "準備段階",
    practicePhase: "練習段階",
    prepTimeLeft: "準備時間残り",
    practiceTimeLeft: "練習時間残り",
    seconds: "秒",
    startRecording: "録音開始",
    stopRecording: "録音停止",
    bombTask: "特別タスク！",
    topic: "トピック",
    resetGame: "ゲームをリセット",
    gameRules: "ゲームルール",
    closeRules: "ルールを閉じる",
    sceneSelectorTitle: "学習シーンを選択",
    ielts_improvement_report: "IELTSスピーキング改善レポート",
    feature_optimizing: "機能最適化",
    report_optimization_message: "私たちはあなたのスピーキングデータを収集して、より個性的なスコアリング提案を提供しています。ありがとうございます！",
    recording_voice: "録音中"
  },
  korean: {
    rollDice: "주사위 굴리기",
    prepPhase: "준비 단계",
    practicePhase: "연습 단계",
    prepTimeLeft: "준비 시간 남음",
    practiceTimeLeft: "연습 시간 남음",
    seconds: "초",
    startRecording: "녹음 시작",
    stopRecording: "녹음 중지",
    bombTask: "특별 과제!",
    topic: "주제",
    resetGame: "게임 재설정",
    gameRules: "게임 규칙",
    closeRules: "규칙 닫기",
    sceneSelectorTitle: "학습 장면 선택",
    ielts_improvement_report: "IELTS 스피킹 개선 보고서",
    feature_optimizing: "기능 최적화",
    report_optimization_message: "우리는 당신의 스피킹 데이터를 수집하여 더욱 개인화된 스코어링 제안을 제공하고 있습니다. 감사합니다!",
    recording_voice: "음성 녹음"
  },
  hindi: {
    rollDice: "पासा फेंको",
    prepPhase: "तैयारी चरण",
    practicePhase: "अभ्यास चरण",
    prepTimeLeft: "तैयारी का समय शेष",
    practiceTimeLeft: "अभ्यास का समय शेष",
    seconds: "सेकंड",
    startRecording: "रिकॉर्डिंग शुरू करें",
    stopRecording: "रिकॉर्डिंग बंद करें",
    bombTask: "विशेष कार्य!",
    topic: "विषय",
    resetGame: "गेम रीसेट करें",
    gameRules: "खेल के नियम",
    closeRules: "नियम बंद करें",
    sceneSelectorTitle: "अध्ययन दृश्य चुनें",
    ielts_improvement_report: "IELTS स्पीकिंग सुधार रिपोर्ट",
    feature_optimizing: "फीचर ऑप्टिमाइज़",
    report_optimization_message: "हम आपके स्पीकिंग डेटा को एक अधिक व्यक्तिगत स्कोरिंग सुझाव प्रदान कर रहे हैं। आपकी समर्थन और समझौते के लिए धन्यवाद!",
    recording_voice: "वॉच रिकॉर्डिंग"
  },
  french: {
    rollDice: "Lancer le dé",
    prepPhase: "Phase de préparation",
    practicePhase: "Phase de pratique",
    prepTimeLeft: "Temps de préparation restant",
    practiceTimeLeft: "Temps de pratique restant",
    seconds: "secondes",
    startRecording: "Commencer l'enregistrement",
    stopRecording: "Arrêter l'enregistrement",
    bombTask: "Tâche spéciale !",
    topic: "Sujet",
    resetGame: "Réinitialiser le jeu",
    gameRules: "Règles du jeu",
    closeRules: "Fermer les règles",
    sceneSelectorTitle: "Choisir une scène d'apprentissage",
    ielts_improvement_report: "Rapport d'amélioration de l'IELTS",
    feature_optimizing: "Optimisation des fonctionnalités",
    report_optimization_message: "Nous collectons vos données de parler pour vous fournir des suggestions d'amélioration personnalisées. Merci pour votre soutien et votre compréhension !",
    recording_voice: "Enregistrement vocal"
  }
};

const TalktasticVoiceGame = React.forwardRef(({ 
  initialScene,
  showRules,
  setShowRules,
  prepTime: initialPrepTime,
  practiceTime: initialPracticeTime,
  onGameComplete,
  onTopicSelected,
  interfaceLanguage = 'english'
}, ref) => {
  const [position, setPosition] = useState(0);
  const [diceValue, setDiceValue] = useState(1);
  const [diceRolling, setDiceRolling] = useState(false);
  const [timer, setTimer] = useState(30);  // 修改默认时间为30秒
  const [gamePhase, setGamePhase] = useState('idle'); // idle, prep, practice, listening
  const [bombTask, setBombTask] = useState(null);
  const [showSceneSelector, setShowSceneSelector] = useState(false);
  const [dropdownPosition] = useState({ top: 60, left: 0, width: 0 });
  const [reachedEnd, setReachedEnd] = useState(false);  // 添加到达终点状态
  const [historyRecords, setHistoryRecords] = useState([]);  // 添加历史记录状态
  // 新增阶段文本状态 - 使用固定值初始化，不使用t函数
  const [phaseText, setPhaseText] = useState(interfaceLanguage === 'english' ? 'waiting to start' : '等待开始');
  // 新增提分报告状态
  const [showScoreReport, setShowScoreReport] = useState(false);
  
  // 语音识别相关
  const recognitionRef = useRef(null);
  const isRecordingRef = useRef(false);
  const fullTranscriptionRef = useRef('');
  const [isRecording, setIsRecording] = useState(false);
  const [transcription, setTranscription] = useState('');
  const [showTranscriptionOptions, setShowTranscriptionOptions] = useState(false);
  
  const [prepTime, setPrepTime] = useState(initialPrepTime !== undefined ? initialPrepTime : 0);  // 使用props值或默认0
  const [practiceTime, setPracticeTime] = useState(initialPracticeTime !== undefined ? initialPracticeTime : 30);  // 使用props值或默认30

  // 监听props变化，更新时间设置
  useEffect(() => {
    if (initialPrepTime !== undefined) {
      setPrepTime(initialPrepTime);
    }
  }, [initialPrepTime]);

  useEffect(() => {
    if (initialPracticeTime !== undefined) {
      setPracticeTime(initialPracticeTime);
    }
  }, [initialPracticeTime]);
  
  // 创建一个固定的关键词到完整问题的映射
  const QUESTION_MAP = {
    "Work/Study": "Do you work or study?",
    "Subject": "What subject are you studying? (Why did you choose it?)",
    "Job": "Do you like your job? (Why?)",
    "Difficulty": "What's the most difficult part of your job/studies?",
    "Hometown": "Where is your hometown?",
    "Hometown likes": "What do you like most about your hometown?",
    "Changes": "Has your hometown changed in recent years?",
    "Recommend": "Would you recommend others to visit your hometown?",
    "Housing": "Do you live in a house or an apartment?",
    "Room": "What's your favorite room in your home?",
    "Home changes": "What would you like to change about your home?",
    "City/Country": "Do you prefer living in the city or the countryside?",
    "Music": "Do you like listening to music? (Why?)",
    "Childhood music": "What kind of music did you listen to as a child?",
    "Live/Recorded": "Do you prefer live music or recorded music?",
    "Music tastes": "Have your music tastes changed over time?",
    "Sports": "Do you like sports? (Why?)",
    "Childhood sports": "What sports did you play as a child?",
    "Future sports": "Is there a sport you'd like to try in the future?",
    "Watch/Play": "Do you prefer watching or playing sports?",
    "Reading": "Do you like reading? (Why?)",
    "Childhood book": "What was your favorite book as a child?",
    "Book format": "Do you prefer e-books or paper books?",
    "Reading frequency": "How often do you read now?",
    "Social media": "Do you use social media often?",
    "Media apps": "What social media app do you like most?",
    "Media impact": "How has social media changed your life?",
    "Communication": "Do you think social media is good for communication?",
    "Devices": "What electronic devices do you use daily?",
    "Tech learning": "Is there a technology you want to learn?",
    "Tech impact": "How has technology changed your work/studies?",
    "Old/New tech": "Do you prefer old or new technology?",
    "Climate": "Are you concerned about climate change?",
    "Environment": "What do you do to protect the environment?",
    "Eco education": "Should schools teach environmental protection?",
    "AI usage": "Do you use AI tools in daily life?",
    "Home robot": "Would you like to have a robot at home?",
    "AI jobs": "Do you think AI will replace human jobs?",
    "Remote work": "Do you like working from home?",
    "Work challenges": "What are the challenges of remote work?",
    "Work trends": "Is remote work common in your country?",
    "Weather": "What's the weather like in your hometown?",
    "Temperature": "Do you prefer hot or cold weather?",
    "Weather mood": "Does weather affect your mood?",
    "Commute": "How do you usually travel to work/school?",
    "Transport": "What's the most popular transportation in your city?",
    "Self-driving": "Would you like to try self-driving cars?",
    "Food": "What's your favorite food?",
    "Cooking": "Do you like cooking?",
    "Foreign food": "Is there a foreign food you'd like to try?",
    // 新添加的问题
    "Clothes shopping": "Where do you usually buy your clothes?",
    "Uniform": "Have you ever worn a uniform?",
    "Fashion trends": "Do most people in your country follow fashion?",
    "Today's weather": "What's the weather like today?",
    "Favourite weather": "What's your favourite weather?",
    "Popular sports": "What is the most popular sport in your country?",
    "Fitness": "How do most people in your country keep fit?",
    "Favourite animal": "What's your favourite animal?",
    "Popular pets": "What is a popular pet to have in your country?",
    "Childhood pet": "Did you have a pet as a child?",
    "Pet ownership": "Why do people have pets?",
    "News sources": "How do you usually get your news?",
    "Newspapers": "Do you often read the newspapers?",
    "News interests": "What kind of news do you usually follow?",
    "News consumption": "How do most people get the news in your country?",
    "International news": "Do you think international news is important?",
    "Neighbours": "Do you like your neighbours?",
    "Neighbour relations": "Are neighbours usually close to each other in your country?",
    "Neighbourhood": "What is your neighbourhood like?",
    "Music education": "Did you learn music at school?",
    "Musical instrument": "If you could learn a musical instrument, what would it be?",
    "Music importance": "Do you think music is important?",
    "Language skills": "How many foreign languages do you speak?",
    "Language learning": "When did you start learning your first foreign language?",
    "Language education": "How many foreign languages do children in your country learn at school?",
    "Language importance": "Do you think it is important to learn a foreign language?",
    "Internet usage": "How often do you go online?",
    "Internet purpose": "What do you use the internet for?",
    "Internet access": "How do you get online?",
    "Computer ownership": "Do you have your own computer?",
    "Favourite website": "What's your favourite website?",
    "Internet safety": "Do you think children should be allowed unsupervised access to the internet?",
    "Social preference": "Do you prefer going out on your own or with friends?",
    "Social frequency": "How often do you go out in a week?",
    "Youth hangouts": "Where do most young people like to go out in your country?",
    "Food dislikes": "Is there any food you dislike?",
    "Traditional meals": "What is a common meal in your country?",
    "Healthy eating": "Do you have a healthy diet?",
    "Fast food": "What do you think of fast food?",
    "Best friend": "Who is your best friend?",
    "Childhood friends": "Are you still friends with people from your childhood?",
    "Family importance": "Is family important in your country?",
    "Evening activities": "Do you ever work or study in the evenings?",
    "Youth evenings": "What is a popular activity for young people in your country in the evenings?",
    "Computer preference": "Do you prefer desktops or laptops?",
    "Computer use": "What do you use your computer for?",
    "Computer importance": "Do you think it is important to learn how to use a computer?"
  };
  
  // 获取t函数
  const { t } = useLanguage();
  
  // 在t函数可用后更新phaseText
  useEffect(() => {
    setPhaseText(t('waiting_to_start'));
  }, [t]);
  
  // Get current scene language settings using useMemo
  const currentLanguageSetting = useMemo(() => {
    return languageSettings[initialScene] || languageSettings['IELTS']; // Default to English
  }, [initialScene]);
  
  // Using useMemo to wrap examTopics object, avoid recreating it on each render
  const examTopics = useMemo(() => {
    // 创建基本的英语话题
    const englishTopics = {
    // English exam topics
    'IELTS': [
      "Family Values", "Online Shopping", "Social Media Influence", "Work-Life Balance", 
      "Public Transportation", "Healthy Lifestyle", "Stress Management", "Community Service", 
      "Digital Addiction", "Fast Food", "Recycling", "Renewable Energy", "Plastic Pollution", 
      "Volunteer Work", "Gender Roles", "Peer Pressure", "Bullying", "Mental Health Awareness", 
      "Parenting Styles", "Elderly Care", "Youth Employment", "Cultural Diversity", "Affordable Housing", 
      "Online Dating"
    ],
    'TOEFL': [
      "Academic Integrity", "Campus Life", "Research Methods", "Critical Thinking", 
      "Study Abroad Programs", "Online Learning", "Scientific Discovery", "Historical Analysis", 
      "Literary Interpretation", "Group Projects", "Class Participation", "Student Organizations", 
      "Academic Writing", "Thesis Development", "College Athletics", "Cultural Exchange Programs", 
      "Student Housing", "Library Resources", "Time Management", "Laboratory Experiments", 
      "Academic Presentations", "Scholarship Applications", "Career Planning", "University Admissions"
    ],
    'CET4': [
      "Friendship", "Part-time Jobs", "Hobbies", "Travel Experience", 
      "Favorite Books", "Movie Reviews", "College Life", "Music Appreciation", 
      "Sports Activities", "Technology Use", "Campus Clubs", "Holiday Celebrations", 
      "Outdoor Adventures", "Learning Methods", "Dormitory Life", "Social Media", 
      "Environmental Protection", "Health Habits", "Family Relationships", "Shopping Habits", 
      "Cultural Differences", "Future Plans", "Hometown Description", "Childhood Memories"
    ],
    'CET6': [
      "Global Economy", "Scientific Research", "Cultural Identity", "International Relations", 
      "Career Development", "Educational Reform", "Technological Innovation", "Social Movements", 
      "Environmental Policies", "Media Influence", "Political Systems", "Population Issues", 
      "Urban Planning", "Natural Disasters", "Climate Change", "Healthcare Systems", 
      "Immigration Policies", "Digital Revolution", "Economic Inequality", "Legal Systems", 
      "International Cooperation", "Human Rights", "Gender Equality", "Globalization Effects"
    ],
    'CPE': [
      "Literary Analysis", "Linguistic Studies", "Cultural Comparison", "Translation Techniques", 
      "Poetry Interpretation", "Academic Writing", "Speech Delivery", "Communication Theories", 
      "English Literature", "Western Culture", "Language Acquisition", "Rhetorical Devices", 
      "Cross-cultural Communication", "Terminology Analysis", "Text Comprehension", "Grammar Structures", 
      "Discourse Analysis", "American Literature", "British Culture", "Language History", 
      "Writing Techniques", "Professional Communication", "Public Speaking", "Media Language"
    ],
    'CAE': [
      "Literary Criticism", "Comparative Literature", "Advanced Linguistics", "Syntax Analysis", 
      "Stylistic Features", "Theoretical Frameworks", "Cultural Anthropology", "Sociolinguistics", 
      "Historical Linguistics", "Semantic Analysis", "Phonological Systems", "Language Philosophy", 
      "Western Civilization", "Literary Movements", "Critical Theory", "Research Methodology", 
      "Discourse Ethics", "Academic Publishing", "Language Policy", "Translation Studies", 
      "Corpus Linguistics", "Pragmatics", "English Etymology", "Cognitive Linguistics"
      ]
    };
    
    // 通用英语话题，用于所有其他语言考试类型
    const commonEnglishTopics = [
      "Family", "Travel", "Food", "Technology", 
      "Education", "Entertainment", "Sports", "Work", 
      "Home", "Shopping", "Weather", "Transportation", 
      "Health", "Hobbies", "Friends", "Culture", 
      "Nature", "City Life", "Holidays", "Communication", 
      "Music", "Films", "Books", "Animals"
    ];
    
    // 创建所有考试类型的映射
    const allExamTypes = {
      // 英语考试已经在englishTopics中定义
      ...englishTopics,
      
      // 法语考试映射到英语话题
      'DELF': commonEnglishTopics,
      'DALF': commonEnglishTopics,
      'TCF': commonEnglishTopics,
      'TEF': commonEnglishTopics,
      
      // 西班牙语考试映射到英语话题
      'DELE': commonEnglishTopics,
      'SIELE': commonEnglishTopics,
      'DIE': commonEnglishTopics,
      'CELU': commonEnglishTopics,
      
      // 日语考试映射到英语话题
      'JLPT': commonEnglishTopics,
      'JPT': commonEnglishTopics,
      'J.TEST': commonEnglishTopics,
      'NAT-TEST': commonEnglishTopics,
      
      // 韩语考试映射到英语话题
      'TOPIK': commonEnglishTopics,
      'KLPT': commonEnglishTopics,
      'FLEX': commonEnglishTopics, 
      'EPS-TOPIK': commonEnglishTopics,
      
      // 葡萄牙语考试映射到英语话题
      'CAPLE': commonEnglishTopics,
      'CELPE-Bras': commonEnglishTopics,
      'PLE': commonEnglishTopics,
      'DEPLE': commonEnglishTopics,
      
      // 德语考试映射到英语话题
      'TestDaF': commonEnglishTopics,
      'Goethe': commonEnglishTopics,
      'DSH': commonEnglishTopics,
      'telc': commonEnglishTopics,
      
      // 意大利语考试映射到英语话题
      'CELI': commonEnglishTopics,
      'CILS': commonEnglishTopics,
      'PLIDA': commonEnglishTopics,
      'IT': commonEnglishTopics,
      
      // 俄语考试映射到英语话题
      'TORFL': commonEnglishTopics,
      'TRKI': commonEnglishTopics,
      'TELC Russian': commonEnglishTopics,
      'RCT': commonEnglishTopics,
      
      // 阿拉伯语考试映射到英语话题
      'ALPT': commonEnglishTopics,
      'AAAT': commonEnglishTopics,
      'ACTFL-OPI': commonEnglishTopics,
      'IB Arabic': commonEnglishTopics,
      
      // 荷兰语考试映射到英语话题
      'CNaVT': commonEnglishTopics,
      'NT2': commonEnglishTopics,
      'CNAVT': commonEnglishTopics,
      'Staatsexamen': commonEnglishTopics
    };
    
    return allExamTypes;
  }, []);
  
  // Helper function to shuffle array - 移到外部作为通用函数
    function shuffleArray(array) {
      const newArray = [...array]; // Create a copy to avoid mutating original
      for (let i = newArray.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [newArray[i], newArray[j]] = [newArray[j], newArray[i]]; // Swap elements
      }
      return newArray;
    }
  
  // Helper function to get a set of board topics for a language - 只返回英语话题
  function getBoardTopics(language) {
    // 雅思Part 1问题和它们的关键词
    const ieltsQuestions = [
      { 
        question: "Do you work or study?", 
        keyword: "Work/Study" 
      },
      { 
        question: "What subject are you studying? (Why did you choose it?)", 
        keyword: "Subject" 
      },
      { 
        question: "Do you like your job? (Why?)", 
        keyword: "Job" 
      },
      { 
        question: "What's the most difficult part of your job/studies?", 
        keyword: "Difficulty" 
      },
      { 
        question: "Where is your hometown?", 
        keyword: "Hometown" 
      },
      { 
        question: "What do you like most about your hometown?", 
        keyword: "Hometown likes" 
      },
      { 
        question: "Has your hometown changed in recent years?", 
        keyword: "Changes" 
      },
      { 
        question: "Would you recommend others to visit your hometown?", 
        keyword: "Recommend" 
      },
      { 
        question: "Do you live in a house or an apartment?", 
        keyword: "Housing" 
      },
      { 
        question: "What's your favorite room in your home?", 
        keyword: "Room" 
      },
      { 
        question: "What would you like to change about your home?", 
        keyword: "Home changes" 
      },
      { 
        question: "Do you prefer living in the city or the countryside?", 
        keyword: "City/Country" 
      },
      { 
        question: "Do you like listening to music? (Why?)", 
        keyword: "Music" 
      },
      { 
        question: "What kind of music did you listen to as a child?", 
        keyword: "Childhood music" 
      },
      { 
        question: "Do you prefer live music or recorded music?", 
        keyword: "Live/Recorded" 
      },
      { 
        question: "Have your music tastes changed over time?", 
        keyword: "Music tastes" 
      },
      { 
        question: "Do you like sports? (Why?)", 
        keyword: "Sports" 
      },
      { 
        question: "What sports did you play as a child?", 
        keyword: "Childhood sports" 
      },
      { 
        question: "Is there a sport you'd like to try in the future?", 
        keyword: "Future sports" 
      },
      { 
        question: "Do you prefer watching or playing sports?", 
        keyword: "Watch/Play" 
      },
      { 
        question: "Do you like reading? (Why?)", 
        keyword: "Reading" 
      },
      { 
        question: "What was your favorite book as a child?", 
        keyword: "Childhood book" 
      },
      { 
        question: "Do you prefer e-books or paper books?", 
        keyword: "Book format" 
      },
      { 
        question: "How often do you read now?", 
        keyword: "Reading frequency" 
      },
      { 
        question: "Do you use social media often?", 
        keyword: "Social media" 
      },
      { 
        question: "What social media app do you like most?", 
        keyword: "Media apps" 
      },
      { 
        question: "How has social media changed your life?", 
        keyword: "Media impact" 
      },
      { 
        question: "Do you think social media is good for communication?", 
        keyword: "Communication" 
      },
      { 
        question: "What electronic devices do you use daily?", 
        keyword: "Devices" 
      },
      { 
        question: "Is there a technology you want to learn?", 
        keyword: "Tech learning" 
      },
      { 
        question: "How has technology changed your work/studies?", 
        keyword: "Tech impact" 
      },
      { 
        question: "Do you prefer old or new technology?", 
        keyword: "Old/New tech" 
      },
      { 
        question: "Are you concerned about climate change?", 
        keyword: "Climate" 
      },
      { 
        question: "What do you do to protect the environment?", 
        keyword: "Environment" 
      },
      { 
        question: "Should schools teach environmental protection?", 
        keyword: "Eco education" 
      },
      { 
        question: "Do you use AI tools in daily life?", 
        keyword: "AI usage" 
      },
      { 
        question: "Would you like to have a robot at home?", 
        keyword: "Home robot" 
      },
      { 
        question: "Do you think AI will replace human jobs?", 
        keyword: "AI jobs" 
      },
      { 
        question: "Do you like working from home?", 
        keyword: "Remote work" 
      },
      { 
        question: "What are the challenges of remote work?", 
        keyword: "Work challenges" 
      },
      { 
        question: "Is remote work common in your country?", 
        keyword: "Work trends" 
      },
      { 
        question: "What's the weather like in your hometown?", 
        keyword: "Weather" 
      },
      { 
        question: "Do you prefer hot or cold weather?", 
        keyword: "Temperature" 
      },
      { 
        question: "Does weather affect your mood?", 
        keyword: "Weather mood" 
      },
      { 
        question: "How do you usually travel to work/school?", 
        keyword: "Commute" 
      },
      { 
        question: "What's the most popular transportation in your city?", 
        keyword: "Transport" 
      },
      { 
        question: "Would you like to try self-driving cars?", 
        keyword: "Self-driving" 
      },
      { 
        question: "What's your favorite food?", 
        keyword: "Food" 
      },
      { 
        question: "Do you like cooking?", 
        keyword: "Cooking" 
      },
      { 
        question: "Is there a foreign food you'd like to try?", 
        keyword: "Foreign food" 
      },
      // 新添加的问题
      { 
        question: "Where do you usually buy your clothes?", 
        keyword: "Clothes shopping" 
      },
      { 
        question: "Have you ever worn a uniform?", 
        keyword: "Uniform" 
      },
      { 
        question: "Do most people in your country follow fashion?", 
        keyword: "Fashion trends" 
      },
      { 
        question: "What's the weather like today?", 
        keyword: "Today's weather" 
      },
      { 
        question: "What's your favourite weather?", 
        keyword: "Favourite weather" 
      },
      { 
        question: "What is the most popular sport in your country?", 
        keyword: "Popular sports" 
      },
      { 
        question: "How do most people in your country keep fit?", 
        keyword: "Fitness" 
      },
      { 
        question: "What's your favourite animal?", 
        keyword: "Favourite animal" 
      },
      { 
        question: "What is a popular pet to have in your country?", 
        keyword: "Popular pets" 
      },
      { 
        question: "Did you have a pet as a child?", 
        keyword: "Childhood pet" 
      },
      { 
        question: "Why do people have pets?", 
        keyword: "Pet ownership" 
      },
      { 
        question: "How do you usually get your news?", 
        keyword: "News sources" 
      },
      { 
        question: "Do you often read the newspapers?", 
        keyword: "Newspapers" 
      },
      { 
        question: "What kind of news do you usually follow?", 
        keyword: "News interests" 
      },
      { 
        question: "How do most people get the news in your country?", 
        keyword: "News consumption" 
      },
      { 
        question: "Do you think international news is important?", 
        keyword: "International news" 
      },
      { 
        question: "Do you like your neighbours?", 
        keyword: "Neighbours" 
      },
      { 
        question: "Are neighbours usually close to each other in your country?", 
        keyword: "Neighbour relations" 
      },
      { 
        question: "What is your neighbourhood like?", 
        keyword: "Neighbourhood" 
      },
      { 
        question: "Did you learn music at school?", 
        keyword: "Music education" 
      },
      { 
        question: "If you could learn a musical instrument, what would it be?", 
        keyword: "Musical instrument" 
      },
      { 
        question: "Do you think music is important?", 
        keyword: "Music importance" 
      },
      { 
        question: "How many foreign languages do you speak?", 
        keyword: "Language skills" 
      },
      { 
        question: "When did you start learning your first foreign language?", 
        keyword: "Language learning" 
      },
      { 
        question: "How many foreign languages do children in your country learn at school?", 
        keyword: "Language education" 
      },
      { 
        question: "Do you think it is important to learn a foreign language?", 
        keyword: "Language importance" 
      },
      { 
        question: "How often do you go online?", 
        keyword: "Internet usage" 
      },
      { 
        question: "What do you use the internet for?", 
        keyword: "Internet purpose" 
      },
      { 
        question: "How do you get online?", 
        keyword: "Internet access" 
      },
      { 
        question: "Do you have your own computer?", 
        keyword: "Computer ownership" 
      },
      { 
        question: "What's your favourite website?", 
        keyword: "Favourite website" 
      },
      { 
        question: "Do you think children should be allowed unsupervised access to the internet?", 
        keyword: "Internet safety" 
      },
      { 
        question: "Do you prefer going out on your own or with friends?", 
        keyword: "Social preference" 
      },
      { 
        question: "How often do you go out in a week?", 
        keyword: "Social frequency" 
      },
      { 
        question: "Where do most young people like to go out in your country?", 
        keyword: "Youth hangouts" 
      },
      { 
        question: "Is there any food you dislike?", 
        keyword: "Food dislikes" 
      },
      { 
        question: "What is a common meal in your country?", 
        keyword: "Traditional meals" 
      },
      { 
        question: "Do you have a healthy diet?", 
        keyword: "Healthy eating" 
      },
      { 
        question: "What do you think of fast food?", 
        keyword: "Fast food" 
      },
      { 
        question: "Who is your best friend?", 
        keyword: "Best friend" 
      },
      { 
        question: "Are you still friends with people from your childhood?", 
        keyword: "Childhood friends" 
      },
      { 
        question: "Is family important in your country?", 
        keyword: "Family importance" 
      },
      { 
        question: "Do you ever work or study in the evenings?", 
        keyword: "Evening activities" 
      },
      { 
        question: "What is a popular activity for young people in your country in the evenings?", 
        keyword: "Youth evenings" 
      },
      { 
        question: "Do you prefer desktops or laptops?", 
        keyword: "Computer preference" 
      },
      { 
        question: "What do you use your computer for?", 
        keyword: "Computer use" 
      },
      { 
        question: "Do you think it is important to learn how to use a computer?", 
        keyword: "Computer importance" 
      },
      { 
        question: "What sports did you play as a child?", 
        keyword: "Childhood sports" 
      },
      { 
        question: "Did you play sport as a child?", 
        keyword: "Childhood sport" 
      },
      { 
        question: "Is there a sport you'd like to try in the future?", 
        keyword: "Future sports" 
      }
    ];
    
    // 确保我们至少有24个问题用于棋盘
    const questionsForBoard = ieltsQuestions.slice(0, 24);
    
    // 打乱问题顺序并返回
    const shuffledQuestions = shuffleArray([...questionsForBoard]);
    
    // 只返回关键词用于棋盘显示
    return shuffledQuestions.map(item => item.keyword);
  }
  
  // 创建一个映射来存储关键词到完整问题的映射
  const keywordToQuestionMap = {};
  
  // 修改更新词汇的函数，同时更新keywordToQuestionMap
  const updateVocab = () => {
    // 确保window.currentWordSetIndex存在
    if (typeof window.currentWordSetIndex === 'undefined') {
      window.currentWordSetIndex = 0;
    }
    
    // Rotate to next word set
    window.currentWordSetIndex = (window.currentWordSetIndex + 1) % 3;
    
    // 获取雅思Part 1问题和关键词
    const ieltsQuestions = [
      { question: "Do you work or study?", keyword: "Work/Study" },
      { question: "What subject are you studying? (Why did you choose it?)", keyword: "Subject" },
      { question: "Do you like your job? (Why?)", keyword: "Job" },
      { question: "What's the most difficult part of your job/studies?", keyword: "Difficulty" },
      { question: "Where is your hometown?", keyword: "Hometown" },
      { question: "What do you like most about your hometown?", keyword: "Hometown likes" },
      { question: "Has your hometown changed in recent years?", keyword: "Changes" },
      { question: "Would you recommend others to visit your hometown?", keyword: "Recommend" },
      { question: "Do you live in a house or an apartment?", keyword: "Housing" },
      { question: "What's your favorite room in your home?", keyword: "Room" },
      { question: "What would you like to change about your home?", keyword: "Home changes" },
      { question: "Do you prefer living in the city or the countryside?", keyword: "City/Country" },
      { question: "Do you like listening to music? (Why?)", keyword: "Music" },
      { question: "What kind of music did you listen to as a child?", keyword: "Childhood music" },
      { question: "Do you prefer live music or recorded music?", keyword: "Live/Recorded" },
      { question: "Have your music tastes changed over time?", keyword: "Music tastes" },
      { question: "Do you like sports? (Why?)", keyword: "Sports" },
      { question: "What sports did you play as a child?", keyword: "Childhood sports" },
      { question: "Is there a sport you'd like to try in the future?", keyword: "Future sports" },
      { question: "Do you prefer watching or playing sports?", keyword: "Watch/Play" },
      { question: "Do you like reading? (Why?)", keyword: "Reading" },
      { question: "What was your favorite book as a child?", keyword: "Childhood book" },
      { question: "Do you prefer e-books or paper books?", keyword: "Book format" },
      { question: "How often do you read now?", keyword: "Reading frequency" },
      { question: "Do you use social media often?", keyword: "Social media" },
      { question: "What social media app do you like most?", keyword: "Media apps" },
      { question: "How has social media changed your life?", keyword: "Media impact" },
      { question: "Do you think social media is good for communication?", keyword: "Communication" },
      { question: "What electronic devices do you use daily?", keyword: "Devices" },
      { question: "Is there a technology you want to learn?", keyword: "Tech learning" },
      { question: "How has technology changed your work/studies?", keyword: "Tech impact" },
      { question: "Do you prefer old or new technology?", keyword: "Old/New tech" },
      { question: "Are you concerned about climate change?", keyword: "Climate" },
      { question: "What do you do to protect the environment?", keyword: "Environment" },
      { question: "Should schools teach environmental protection?", keyword: "Eco education" },
      { question: "Do you use AI tools in daily life?", keyword: "AI usage" },
      { question: "Would you like to have a robot at home?", keyword: "Home robot" },
      { question: "Do you think AI will replace human jobs?", keyword: "AI jobs" },
      { question: "Do you like working from home?", keyword: "Remote work" },
      { question: "What are the challenges of remote work?", keyword: "Work challenges" },
      { question: "Is remote work common in your country?", keyword: "Work trends" },
      { question: "What's the weather like in your hometown?", keyword: "Weather" },
      { question: "Do you prefer hot or cold weather?", keyword: "Temperature" },
      { question: "Does weather affect your mood?", keyword: "Weather mood" },
      { question: "How do you usually travel to work/school?", keyword: "Commute" },
      { question: "What's the most popular transportation in your city?", keyword: "Transport" },
      { question: "Would you like to try self-driving cars?", keyword: "Self-driving" },
      { question: "What's your favorite food?", keyword: "Food" },
      { question: "Do you like cooking?", keyword: "Cooking" },
      { question: "Is there a foreign food you'd like to try?", keyword: "Foreign food" },
      // 新添加的问题
      { question: "Where do you usually buy your clothes?", keyword: "Clothes shopping" },
      { question: "Have you ever worn a uniform?", keyword: "Uniform" },
      { question: "Do most people in your country follow fashion?", keyword: "Fashion trends" },
      { question: "What's the weather like today?", keyword: "Today's weather" },
      { question: "What's your favourite weather?", keyword: "Favourite weather" },
      { question: "What is the most popular sport in your country?", keyword: "Popular sports" },
      { question: "How do most people in your country keep fit?", keyword: "Fitness" },
      { question: "What's your favourite animal?", keyword: "Favourite animal" },
      { question: "What is a popular pet to have in your country?", keyword: "Popular pets" },
      { question: "Did you have a pet as a child?", keyword: "Childhood pet" },
      { question: "Why do people have pets?", keyword: "Pet ownership" },
      { question: "How do you usually get your news?", keyword: "News sources" },
      { question: "Do you often read the newspapers?", keyword: "Newspapers" },
      { question: "What kind of news do you usually follow?", keyword: "News interests" },
      { question: "How do most people get the news in your country?", keyword: "News consumption" },
      { question: "Do you think international news is important?", keyword: "International news" },
      { question: "Do you like your neighbours?", keyword: "Neighbours" },
      { question: "Are neighbours usually close to each other in your country?", keyword: "Neighbour relations" },
      { question: "What is your neighbourhood like?", keyword: "Neighbourhood" },
      { question: "Did you learn music at school?", keyword: "Music education" },
      { question: "If you could learn a musical instrument, what would it be?", keyword: "Musical instrument" },
      { question: "Do you think music is important?", keyword: "Music importance" },
      { question: "How many foreign languages do you speak?", keyword: "Language skills" },
      { question: "When did you start learning your first foreign language?", keyword: "Language learning" },
      { question: "How many foreign languages do children in your country learn at school?", keyword: "Language education" },
      { question: "Do you think it is important to learn a foreign language?", keyword: "Language importance" },
      { question: "How often do you go online?", keyword: "Internet usage" },
      { question: "What do you use the internet for?", keyword: "Internet purpose" },
      { question: "How do you get online?", keyword: "Internet access" },
      { question: "Do you have your own computer?", keyword: "Computer ownership" },
      { question: "What's your favourite website?", keyword: "Favourite website" },
      { question: "Do you think children should be allowed unsupervised access to the internet?", keyword: "Internet safety" },
      { question: "Do you prefer going out on your own or with friends?", keyword: "Social preference" },
      { question: "How often do you go out in a week?", keyword: "Social frequency" },
      { question: "Where do most young people like to go out in your country?", keyword: "Youth hangouts" },
      { question: "Is there any food you dislike?", keyword: "Food dislikes" },
      { question: "What is a common meal in your country?", keyword: "Traditional meals" },
      { question: "Do you have a healthy diet?", keyword: "Healthy eating" },
      { question: "What do you think of fast food?", keyword: "Fast food" },
      { question: "Who is your best friend?", keyword: "Best friend" },
      { question: "Are you still friends with people from your childhood?", keyword: "Childhood friends" },
      { question: "Is family important in your country?", keyword: "Family importance" },
      { question: "Do you ever work or study in the evenings?", keyword: "Evening activities" },
      { question: "What is a popular activity for young people in your country in the evenings?", keyword: "Youth evenings" },
      { question: "Do you prefer desktops or laptops?", keyword: "Computer preference" },
      { question: "What do you use your computer for?", keyword: "Computer use" },
      { question: "Do you think it is important to learn how to use a computer?", keyword: "Computer importance" },
      { question: "What sports did you play as a child?", keyword: "Childhood sports" },
      { question: "Did you play sport as a child?", keyword: "Childhood sport" },
      { question: "Is there a sport you'd like to try in the future?", keyword: "Future sports" }
    ];
    
    // 固定取24个问题，确保始终有足够的题目
    const questionsPerSet = 24;
    const totalSets = Math.floor(ieltsQuestions.length / questionsPerSet);
    
    // 确保window.currentWordSetIndex在有效范围内
    window.currentWordSetIndex = window.currentWordSetIndex % totalSets;
    
    // 计算起始索引，确保不超出范围
    const startIndex = window.currentWordSetIndex * questionsPerSet;
    const endIndex = Math.min(startIndex + questionsPerSet, ieltsQuestions.length);
    
    // 获取当前集合的问题
    let questionsSubset = ieltsQuestions.slice(startIndex, endIndex);
    
    // 确保有足够的问题，如果不足24个，从开头补足
    if (questionsSubset.length < questionsPerSet) {
      const remaining = questionsPerSet - questionsSubset.length;
      questionsSubset = [...questionsSubset, ...ieltsQuestions.slice(0, remaining)];
    }
    
    // 打乱问题顺序
    const shuffledQuestions = shuffleArray([...questionsSubset]);
    
    // 确保至少有24个问题可用于棋盘
    if (shuffledQuestions.length < 24) {
      console.error('警告：问题数量不足24个，使用默认问题集');
      // 使用前24个问题作为默认问题集
      const defaultQuestions = ieltsQuestions.slice(0, 24);
      const shuffledDefault = shuffleArray([...defaultQuestions]);
      
      // 更新关键词到问题的映射
      shuffledDefault.forEach(item => {
        QUESTION_MAP[item.keyword] = item.question;
      });
      
      // 提取关键词用于棋盘显示
      const defaultTopics = shuffledDefault.map(item => item.keyword);
      setCurrentTopics(defaultTopics);
      return;
    }
    
    // 更新关键词到问题的映射
    shuffledQuestions.forEach(item => {
      QUESTION_MAP[item.keyword] = item.question;
    });
    
    // 提取关键词用于棋盘显示
    const newTopics = shuffledQuestions.map(item => item.keyword);
    console.log('更新词库：新话题数量', newTopics.length);
    
    // 仅更新词汇内容，不重置玩家位置和游戏状态
    setCurrentTopics(newTopics);
    
    // 保留玩家当前位置，如果位置超出新主题数组长度，则调整到有效范围内
    setPosition(prevPosition => {
      if (prevPosition >= newTopics.length) {
        return newTopics.length - 1;
      }
      return prevPosition;
    });
    
    // 清理录音相关的内容，但不重置游戏状态
    if (gamePhase === 'idle') {
      // 只有在闲置状态才清除录音内容，避免打断正在进行的练习
      setTranscription('');
      fullTranscriptionRef.current = '';
    }
  };

  // 初始化关键词到问题的映射
  useEffect(() => {
    // 获取雅思Part 1问题和关键词
    const ieltsQuestions = [
      { 
        question: "Do you work or study?", 
        keyword: "Work/Study" 
      },
      { 
        question: "What subject are you studying? (Why did you choose it?)", 
        keyword: "Subject" 
      },
      { 
        question: "Do you like your job? (Why?)", 
        keyword: "Job" 
      },
      // ... 其他问题 ...
    ];
    
    // 初始化映射
    ieltsQuestions.forEach(item => {
      keywordToQuestionMap[item.keyword] = item.question;
    });
  }, [keywordToQuestionMap]); // 添加keywordToQuestionMap作为依赖项

  // 添加状态来存储当前问题
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [showQuestionText, setShowQuestionText] = useState(false);

  // 文字转语音功能 - 确保使用完整问题，并在播放时暂停录音
  const speakText = (text) => {
    console.log("朗读文本:", text); // 调试用
    
    // 检查浏览器是否支持语音合成
    if ('speechSynthesis' in window) {
      // 如果当前在录音，先暂停录音
      const wasRecording = isRecordingRef.current;
      if (wasRecording && recognitionRef.current) {
        console.log("暂停录音以播放语音");
        recognitionRef.current.stop();
        isRecordingRef.current = false;
        if (setIsRecording) setIsRecording(false);
      }
      
      // 创建语音合成实例
      const utterance = new SpeechSynthesisUtterance(text);
      
      // 设置语音参数
      utterance.lang = 'en-US'; // 英语发音
      utterance.rate = 0.9; // 语速稍慢一些，更清晰
      utterance.pitch = 1; // 默认音调
      utterance.volume = 1; // 最大音量
      
      // 播放完成后恢复录音
      utterance.onend = () => {
        console.log("语音播放完成");
        // 延迟500毫秒后恢复录音，给用户一点准备时间
        if (wasRecording) {
          setTimeout(() => {
            console.log("恢复录音");
            startSpeechRecognition();
          }, 500);
        }
      };
      
      // 停止之前可能正在播放的语音
      window.speechSynthesis.cancel();
      
      // 播放语音
      window.speechSynthesis.speak(utterance);
    } else {
      console.error('当前浏览器不支持文字转语音功能');
    }
  };

  // 修改发送语音消息的功能，添加控制属性
  const sendVoiceMessage = (keyword) => {
    // 如果已经到达终点，不播放语音
    if (reachedEnd) {
      return;
    }

    // 获取关键词对应的完整问题
    const fullQuestion = QUESTION_MAP[keyword] || keyword;
    console.log("发送语音消息, 关键词:", keyword, "完整问题:", fullQuestion); // 调试用
    
    if (onTopicSelected && fullQuestion) {
      // 创建一个语音消息对象，添加控制属性
      const voiceMessage = {
        type: 'mentor',
        isVoice: true, // 标记为语音消息
        content: fullQuestion, // 存储完整问题文本供后续使用
        time: new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' }),
        controls: {
          canReplay: true, // 允许重播
          canShowText: true, // 允许显示原文
          keyword: keyword, // 保存关键词，用于重播功能
        }
      };
      
      // 发送语音消息对象到父组件
      onTopicSelected(null, voiceMessage);
      
      // 播放语音
      speakText(fullQuestion);
      
      // 存储当前问题
      setCurrentQuestion(fullQuestion);
      // 重置显示文本状态
      setShowQuestionText(false);
    }
  };

  // 修改重播问题功能，让父组件能够调用
  const replayCurrentQuestion = (keyword) => {
    // 如果提供了关键词，使用它；否则使用当前位置的关键词
    const currentKeyword = keyword || currentTopics[position];
    
    if (currentKeyword) {
      // 获取关键词对应的完整问题
      const fullQuestion = QUESTION_MAP[currentKeyword] || currentKeyword;
      
      // 播放语音
      speakText(fullQuestion);
    }
  };

  // 修改显示原文功能，让父组件能够调用
  const showOriginalQuestion = (keyword) => {
    // 如果提供了关键词，使用它；否则使用当前位置的关键词
    const currentKeyword = keyword || currentTopics[position];
    
    if (currentKeyword) {
      // 获取关键词对应的完整问题
      const fullQuestion = QUESTION_MAP[currentKeyword] || currentKeyword;
      
      // 发送文本格式的完整问题到聊天框
      const mentorMessage = {
        type: 'mentor',
        isVoice: false, // 标记为普通文本消息
        content: fullQuestion,
        time: new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })
      };
      
      if (onTopicSelected) {
        onTopicSelected(null, mentorMessage);
      }
      
      // 设置状态，防止重复发送
      setShowQuestionText(true);
    }
  };
  
  // Set default topics to IELTS
  const [currentTopics, setCurrentTopics] = useState(examTopics[initialScene] || examTopics['IELTS']);
  
  // 初始化语音识别
  useEffect(() => {
    // Check if browser supports speech recognition API
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      console.error('Your browser does not support speech recognition');
      return;
    }
    
    // Create speech recognition instance
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    recognitionRef.current = new SpeechRecognition();
    
    // Configure speech recognition
    recognitionRef.current.continuous = true;
    recognitionRef.current.interimResults = true;
    
    // Set speech recognition language
    recognitionRef.current.lang = currentLanguageSetting.code;
    
    // Handle recognition results
    recognitionRef.current.onresult = (event) => {
      const results = event.results;
      let interimTranscript = '';
      let finalTranscript = '';
      
      for (let i = event.resultIndex; i < results.length; i++) {
        const transcript = results[i][0].transcript;
        if (results[i].isFinal) {
          finalTranscript += transcript + ' ';
        } else {
          interimTranscript += transcript;
        }
      }
      
      // 更新完整的转录内容
      if (finalTranscript) {
        fullTranscriptionRef.current = (fullTranscriptionRef.current + ' ' + finalTranscript).trim();
      }
      
      // 更新显示的内容
      setTranscription(fullTranscriptionRef.current + ' ' + interimTranscript);
    };
    
    // Handle speech recognition end
    recognitionRef.current.onend = () => {
      if (isRecording && gamePhase === 'practice') {
        // If still in practice phase and should be recording, restart
        try {
          recognitionRef.current.start();
        } catch (e) {
          console.error('Failed to restart speech recognition:', e);
          setIsRecording(false);
        }
      } else {
        setIsRecording(false);
      }
    };
    
    // Handle speech recognition errors
    recognitionRef.current.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      if (event.error === 'not-allowed') {
        alert('Please allow microphone access to enable speech recognition');
      }
      setIsRecording(false);
    };
    
    // Start speech recognition when entering practice phase
    if (gamePhase === 'practice' && !isRecording) {
      try {
        recognitionRef.current.start();
        setIsRecording(true);
      } catch (e) {
        console.error('Unable to start speech recognition:', e);
      }
    }
    
    return () => {
      // Clean up speech recognition
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (e) {
          // Ignore errors
        }
      }
    };
  }, [isRecording, gamePhase, currentLanguageSetting]);
  
  // Game timer
  useEffect(() => {
    if (gamePhase === 'idle' || gamePhase === 'completed') {
      return;
    }
    
    console.log('当前游戏阶段:', gamePhase); // 添加调试信息
    
    const countdown = setInterval(() => {
      setTimer(prevTimer => {
        // 当倒计时到达5秒时，如果处于准备阶段或听题阶段，提前启动录音
        if (prevTimer === 5 && (gamePhase === 'prep' || gamePhase === 'listening')) {
          console.log('提前5秒开始录音');
          startSpeechRecognition();
        }
        
        if (prevTimer <= 1) {
          clearInterval(countdown);
          
          if (gamePhase === 'listening') {
            // 听题阶段结束，直接进入练习阶段
            console.log('听题阶段结束，开始练习阶段');
            setGamePhase('practice');
            setPhaseText(t('answer_phase'));
            setTimer(practiceTime);
            // 录音已在5秒前开始，无需再次启动
          } else if (gamePhase === 'prep') {
            // 准备阶段结束，开始练习阶段
            console.log('准备阶段结束，开始练习阶段');
            setGamePhase('practice');
            setPhaseText(t('preparation_phase_part1'));
            setTimer(practiceTime);
            // 录音已在5秒前开始，无需再次启动
          } else if (gamePhase === 'practice') {
            // 练习阶段结束，但不立即停止录音，延迟5秒后停止
            console.log('练习阶段结束，延迟5秒后停止录音和回到空闲状态');
            
            // 设置一个5秒的延迟，延迟后再停止录音和设置游戏状态
            setTimeout(() => {
              // 停止录音
              if (recognitionRef.current) {
                try {
                  recognitionRef.current.stop();
                } catch (e) {
                  console.error('停止录音时出错:', e);
                }
              }
              
              setGamePhase('idle');
              setPhaseText(t('waiting_to_start'));
              
              // 将练习内容添加到历史记录
              if (fullTranscriptionRef.current.trim()) {
                setHistoryRecords(prev => [...prev, {
                  topic: currentTopics[position],
                  content: fullTranscriptionRef.current.trim(),
                  timestamp: new Date().toLocaleString()
                }]);
              }
              
              // 发送最终的转录内容
              if (onTopicSelected && fullTranscriptionRef.current) {
                const finalMessage = {
                  type: 'user',
                  content: fullTranscriptionRef.current.trim(),
                  time: new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })
                };
                onTopicSelected(null, finalMessage);
              }
              
              // 只有当到达终点时才显示历史记录框
              if (reachedEnd) {
                setShowTranscriptionOptions(true);
              }
            }, 5000); // 延迟5秒
          }
          
          return 0;
        }
        return prevTimer - 1;
      });
    }, 1000);
    
    return () => {
      if (countdown) {
        clearInterval(countdown);
      }
    };
  }, [gamePhase, onTopicSelected, practiceTime, currentTopics, position, reachedEnd]);
  
  // Update topics when scene/language changes
  useEffect(() => {
    const langSetting = currentLanguageSetting;
    const newTopics = getBoardTopics(langSetting.name);
    setCurrentTopics(newTopics);
  }, [initialScene, currentLanguageSetting]);
  
  const adjustPrepTime = (amount) => {
    setPrepTime(Math.max(0, prepTime + amount * 5));  // 修改为5秒的步长
  };
  
  const adjustPracticeTime = (amount) => {
    setPracticeTime(Math.max(0, practiceTime + amount * 5));  // 修改最小值为0
  };
  
  // 手绘风格的按钮SVG
  const HandDrawnButton = ({ type, size = 20 }) => {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" className="mx-auto">
        <filter id="shaky-button">
          <feTurbulence type="turbulence" baseFrequency="0.05" numOctaves="2" result="turbulence"/>
          <feDisplacementMap in="SourceGraphic" in2="turbulence" scale="1.5" xChannelSelector="R" yChannelSelector="G"/>
        </filter>
        <g style={{ filter: 'url(#shaky-button)' }}>
          {type === 'plus' ? (
            <>
              <circle cx="12" cy="12" r="10" fill="none" stroke="#1e62d0" strokeWidth="1.5" />
              <path d="M7,12 L17,12 M12,7 L12,17" fill="none" stroke="#1e62d0" strokeWidth="2" strokeLinecap="round" />
            </>
          ) : (
            <>
              <circle cx="12" cy="12" r="10" fill="none" stroke="#1e62d0" strokeWidth="1.5" />
              <path d="M7,12 L17,12" fill="none" stroke="#1e62d0" strokeWidth="2" strokeLinecap="round" />
            </>
          )}
        </g>
      </svg>
    );
  };
  
  // 手绘闹钟SVG
  const HandDrawnClock = () => (
    <div className="relative">
      <svg width="120" height="120" viewBox="0 0 100 100" className="mx-auto">
        <filter id="shaky">
          <feTurbulence type="turbulence" baseFrequency="0.05" numOctaves="2" result="turbulence"/>
          <feDisplacementMap in="SourceGraphic" in2="turbulence" scale="2" xChannelSelector="R" yChannelSelector="G"/>
        </filter>
        <g style={{ filter: 'url(#shaky)' }}>
          <circle cx="50" cy="50" r="40" fill="none" stroke="#1e62d0" strokeWidth="3" strokeLinecap="round" />
          <path d="M50,20 L50,50 L70,50" fill="none" stroke="#1e62d0" strokeWidth="3" strokeLinecap="round" strokeDasharray={gamePhase !== 'idle' ? "1,0" : "1,1"} strokeDashoffset={timer % 10}>
            <animateTransform 
              attributeName="transform" 
              attributeType="XML" 
              type="rotate" 
              from="0 50 50" 
              to="360 50 50" 
              dur={gamePhase !== 'idle' ? "60s" : "0s"}
              repeatCount="indefinite" 
            />
          </path>
          <circle cx="50" cy="50" r="4" fill="#1e62d0" />
          <path d="M50,10 L50,15 M90,50 L85,50 M50,90 L50,85 M10,50 L15,50" stroke="#1e62d0" strokeWidth="2" />
          <path d="M25,20 L28,25 M80,25 L75,28 M80,75 L75,72 M25,80 L28,75" stroke="#1e62d0" strokeWidth="1.5" />
          <path d="M40,16 C25,20 15,30 15,50 C15,75 35,85 50,85 C65,85 85,75 85,50 C85,25 65,15 50,15" fill="none" stroke="#1e62d0" strokeWidth="0.5" opacity="0.3" />
        </g>
      </svg>
    </div>
  );

  // 定义游戏规则
  const gameRules = `Game Rules:
  1. Click the "Roll Dice" button to start the game.
  2. Move to the corresponding topic square based on the dice roll.
  3. The system will give you time to prepare for the topic.
  4. When preparation time ends, recording will automatically begin, and you'll need to discuss the topic in your selected language.
  5. After finishing, you can review your speaking content.
  6. If you land on a square with a bomb symbol, you'll need to complete an additional challenge.
  
  Tips:
  - Try to use complete sentences and rich vocabulary to express your views.
  - Consider exploring topics from multiple angles (Who/When/Where/Why).
  - Speech recognition uses the selected language, so try to speak clearly.`;

  // 定义骰子图标
  const diceIcons = [
    <Dice1 key="dice1" size={48} color="#3182ce" />,
    <Dice2 key="dice2" size={48} color="#3182ce" />,
    <Dice3 key="dice3" size={48} color="#3182ce" />,
    <Dice4 key="dice4" size={48} color="#3182ce" />,
    <Dice5 key="dice5" size={48} color="#3182ce" />,
    <Dice6 key="dice6" size={48} color="#3182ce" />
  ];

  // 定义炸弹位置
  const bombPositions = [4, 11, 19];

  // 更新到特定的考试类型
  const changeExamType = (examType) => {
    setCurrentTopics(examTopics[examType]);
    setPosition(0);
    setGamePhase('idle');
    setTimer(30);
    setBombTask(null);
    setShowSceneSelector(false);
    setIsRecording(false);
    setTranscription('');
    setShowTranscriptionOptions(false);
    
    // If recording, stop first
    if (isRecording && recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (e) {
        // Ignore errors
      }
      setIsRecording(false);
    }
    
    // Send custom event to notify App component that scene has changed
    const event = new CustomEvent('sceneChange', { detail: { scene: examType } });
    window.dispatchEvent(event);
  };

  // 重置游戏的函数
  const resetGame = () => {
    // 重置游戏状态
    setPosition(0);
    setDiceValue(1);
    setTimer(30);
    setGamePhase('idle');
    setBombTask(null);
    setReachedEnd(false);
    setShowTranscriptionOptions(false);
    fullTranscriptionRef.current = '';
    setTranscription('');
    setPhaseText(t('waiting_to_start'));
    setHistoryRecords([]);
  };

  // 查看提分报告
  const viewScoreReport = () => {
    // 如果有 onGameComplete 回调，先关闭选项弹窗，然后调用回调
    if (onGameComplete) {
      setShowTranscriptionOptions(false);
      const selectedKeyword = currentTopics[currentTopics.length - 1];
      onGameComplete(QUESTION_MAP[selectedKeyword] || selectedKeyword);
    } else {
      // 否则显示提分报告模态框
    setShowScoreReport(true);
    // 关闭抽签结束选项弹窗
    setShowTranscriptionOptions(false);
    }
  };

  // 添加导出对话记录的函数
  const exportHistory = () => {
    // 如果没有历史记录，则提示用户
    if (historyRecords.length === 0) {
      alert(interfaceLanguage === 'english' ? 'No conversation records to export' : '暂无对话记录可导出');
      return;
    }
    
    // 创建导出内容 - 根据当前界面语言选择输出语言
    let exportContent = '';
    
    if (interfaceLanguage === 'english') {
      exportContent = "Talktastic Speaking Practice Records\n\n";
      
      // 添加历史记录，按照优化格式：时间、完整问题、用户回答
      historyRecords.forEach((record, index) => {
        // 获取关键词对应的完整问题
        const fullQuestion = QUESTION_MAP[record.topic] || record.topic;
        
        exportContent += `------ Record ${index + 1} ------\n`;
        exportContent += `Time: ${record.timestamp}\n`;
        exportContent += `Question: ${fullQuestion}\n`;
        exportContent += `Answer: ${record.content}\n\n`;
      });
    } else {
      exportContent = "Talktastic口语练习记录\n\n";
    
    // 添加系统保存期限提示
    exportContent += "提示：系统记录只保存三天，请妥善保管此文件\n\n";
    
    // 添加历史记录，按照优化格式：时间、完整问题、用户回答
    historyRecords.forEach((record, index) => {
      // 获取关键词对应的完整问题
      const fullQuestion = QUESTION_MAP[record.topic] || record.topic;
      
      exportContent += `------ 记录 ${index + 1} ------\n`;
      exportContent += `时间: ${record.timestamp}\n`;
      exportContent += `问题: ${fullQuestion}\n`;
      exportContent += `回答: ${record.content}\n\n`;
    });
    }
    
    // 创建Blob对象
    const blob = new Blob([exportContent], { type: 'text/plain;charset=utf-8' });
    
    // 创建下载链接
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    
    // 添加日期到文件名
    const today = new Date();
    const dateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    
    // 根据界面语言设置文件名
    if (interfaceLanguage === 'english') {
      a.download = `talktastic-records-${dateStr}.txt`;
    } else {
    a.download = `talktastic-记录-${dateStr}.txt`;
    }
    
    document.body.appendChild(a);
    a.click();
    
    // 清理
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 0);
  };

  // 修改 rollDice 函数，为准备时间小的情况增加听题时间
  const rollDice = () => {
    if (gamePhase === 'idle') {
      // 如果准备时间和练习时间都为0，直接跳转到终点
      if (prepTime === 0 && practiceTime === 0) {
        setPosition(currentTopics.length - 1);
        setReachedEnd(true);
        setShowTranscriptionOptions(true);
        
        // 添加对游戏完成事件的调用
        if (onGameComplete) {
          const selectedKeyword = currentTopics[currentTopics.length - 1];
          onGameComplete(QUESTION_MAP[selectedKeyword] || selectedKeyword);
        }
        
        return;
      }

      // 开始骰子滚动动画
      setDiceRolling(true);
      
      // 模拟骰子滚动过程中的变化
      let rollCount = 0;
      const maxRolls = 10; // 滚动10次
      const rollInterval = setInterval(() => {
        rollCount++;
        // 随机骰子点数（1-6）
        const randomValue = Math.floor(Math.random() * 6) + 1;
        setDiceValue(randomValue);
        
        // 滚动结束时停止并设置最终值
        if (rollCount >= maxRolls) {
          clearInterval(rollInterval);
          const finalValue = Math.floor(Math.random() * 6) + 1;
          setDiceValue(finalValue);
          setDiceRolling(false);
          
          // 更新位置到新的骰子值对应的格子，判断是否到达终点
          setPosition((prevPosition) => {
            const newPosition = prevPosition + finalValue;
            
            // 如果新位置达到或超过棋盘末尾，则停在最后一格
            if (newPosition >= currentTopics.length - 1) {
              // 设置游戏为已完成状态
              setReachedEnd(true);
              setShowTranscriptionOptions(true);
              // 获取当前选中的话题 (最后一个话题)
              const selectedKeyword = currentTopics[currentTopics.length - 1];
              
              // 不再发送语音消息
              // sendVoiceMessage(selectedKeyword);
              
              // 调用游戏完成回调函数
              if (onGameComplete) {
                onGameComplete(QUESTION_MAP[selectedKeyword] || selectedKeyword);
              }
              
              // 返回最后一个位置
              return currentTopics.length - 1;
            }
            
            // 否则正常移动
            return newPosition;
          });
          
          // 获取当前选中的话题 (如果没有到达终点)
          if (!reachedEnd) {
            const selectedPosition = (position + finalValue >= currentTopics.length) 
              ? currentTopics.length - 1 
              : position + finalValue;
            const selectedKeyword = currentTopics[selectedPosition];
            
            // 发送语音消息，使用完整问题
            sendVoiceMessage(selectedKeyword);
          }
          
          // 如果到达终点，直接显示历史记录框，不进入练习阶段
          if (position + finalValue >= currentTopics.length - 1) {
            setReachedEnd(true);
            setShowTranscriptionOptions(true);
            
            // 添加对游戏完成事件的调用
            if (onGameComplete) {
              const selectedKeyword = currentTopics[currentTopics.length - 1];
              onGameComplete(QUESTION_MAP[selectedKeyword] || selectedKeyword);
            }
            
            return;
          }
          
          // 确定最小的听题/准备时间
          if (prepTime <= 5) {
            // 准备时间小于等于5秒时，提供10秒的听题时间
            setGamePhase('listening'); // 新阶段：听题阶段
            setTimer(10);
            // 显示"请听题"
            setPhaseText(t('please_listen'));
            // 清除之前的录音内容
            fullTranscriptionRef.current = '';
            setTranscription('');
      } else {
            // 准备时间大于5秒，正常进入准备阶段
        setGamePhase('prep');
        setTimer(prepTime);
            // 显示"准备阶段"和"请听题并准备"
            setPhaseText(t('preparation_phase_part1'));
            // 清除之前的录音内容
            fullTranscriptionRef.current = '';
            setTranscription('');
          }
        }
      }, 100); // 每100毫秒改变一次骰子点数
    }
  };

  // 添加启动语音识别的辅助函数
  const startSpeechRecognition = () => {
    console.log("开始语音识别");
    if (window.SpeechRecognition || window.webkitSpeechRecognition) {
      // 如果还没有创建语音识别实例，创建一个
      if (!recognitionRef.current) {
        console.log("创建新的语音识别实例");
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        recognitionRef.current = new SpeechRecognition();
        recognitionRef.current.continuous = true;
        recognitionRef.current.interimResults = true;
        recognitionRef.current.lang = currentLanguageSetting.code; // 使用当前语言设置
        
        // 处理识别结果
        recognitionRef.current.onresult = (event) => {
          let interimTranscript = '';
          let finalTranscript = '';
          
          for (let i = event.resultIndex; i < event.results.length; i++) {
            const transcript = event.results[i][0].transcript;
            if (event.results[i].isFinal) {
              finalTranscript += transcript + ' ';
            } else {
              interimTranscript += transcript;
            }
          }
          
          // 更新完整的转录内容
          if (finalTranscript) {
            fullTranscriptionRef.current += finalTranscript;
            console.log("最终转录内容更新:", finalTranscript);
          }
          
          // 更新显示的内容
          setTranscription(fullTranscriptionRef.current + ' ' + interimTranscript);
        };
        
        // 处理错误
        recognitionRef.current.onerror = (event) => {
          console.error("语音识别错误:", event.error);
          
          // 如果是iPad Safari上的错误，不显示错误提示，因为我们使用了替代方案
          if (!isIPadSafari()) {
            if (event.error === 'no-speech') {
              console.log('未检测到语音');
            } else {
              alert(`语音识别错误: ${event.error}`);
            }
          }
        };
        
        // 处理结束
        recognitionRef.current.onend = () => {
          console.log("语音识别结束");
          
          // 在iPad Safari上，我们使用的是替代方案，不需要在这里重新启动
          if (!isIPadSafari() && gamePhase === 'practice') {
            // 如果仍在练习阶段，自动重新开始录音
            try {
              console.log("重新启动语音识别");
              recognitionRef.current.start();
            } catch (e) {
              console.error("重新启动语音识别失败:", e);
            }
          }
        };
      }
      
      // 重置转录内容
      fullTranscriptionRef.current = '';
      setTranscription('');
      
      // 开始识别
      try {
        recognitionRef.current.start();
        console.log("语音识别已启动");
      } catch (e) {
        console.error("启动语音识别失败:", e);
        alert('启动语音识别失败，请刷新页面重试');
      }
    } else {
      console.error("浏览器不支持语音识别");
      alert('您的浏览器不支持语音识别功能，请使用Chrome浏览器');
    }
  };

  // 初始化组件并设置默认状态
  useEffect(() => {
    // 设置默认阶段文本
    setPhaseText(gamePhase === 'idle' ? t('waiting_to_start') : 
                 gamePhase === 'prep' ? t('preparation_phase_part1') : 
                 gamePhase === 'practice' ? t('answer_phase') : 
                 gamePhase === 'listening' ? t('please_listen') : '');

    // 当游戏处于非idle状态且超过10分钟没有交互时，自动重置游戏
    const resetTimeout = setTimeout(() => {
      if (gamePhase !== 'idle') {
        setGamePhase('idle');
        setPhaseText(t('waiting_to_start'));
        setTimer(practiceTime);
      }
    }, 10 * 60 * 1000); // 10分钟

    return () => clearTimeout(resetTimeout);
  }, []);

  // 暴露方法给父组件
  useImperativeHandle(ref, () => ({
    replayQuestion: replayCurrentQuestion,
    showQuestionText: showOriginalQuestion,
    resetGame,
    updateVocab,
    rollDice
  }));

  return (
    <div className="p-4 max-w-7xl mx-auto bg-white rounded-lg shadow-lg" style={{ paddingRight: '2rem' }}>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-blue-600"></h1>
      </div>

      {showRules && (
        <Alert className="mb-4">
          <AlertTitle>Game Rules</AlertTitle>
          <AlertDescription className="whitespace-pre-line">{gameRules}</AlertDescription>
        </Alert>
      )}

      <div className="game-container">
        {/* Left side time settings area */}
        <div className="time-settings-area bg-blue-50 p-4 rounded-lg shadow-md border-2 border-blue-100">
          <h2 className="text-xl font-bold text-center mb-4 text-blue-700">Time Settings</h2>
          
          <div className="mb-8">
            <HandDrawnClock />
          </div>
          
          <div className="mb-6 bg-white p-3 rounded-lg shadow-sm border border-blue-100">
            <h3 className="font-semibold mb-2 text-blue-800">Preparation Time</h3>
            <div className="flex items-center justify-between">
              <button 
                onClick={() => adjustPrepTime(-1)} 
                className="p-2 bg-blue-100 rounded-full hover:bg-blue-200 transition-all transform hover:scale-110 disabled:opacity-50"
                disabled={gamePhase !== 'idle'}
              >
                <HandDrawnButton type="minus" />
              </button>
              <span className="text-2xl font-bold text-blue-600">{prepTime}s</span>
              <button 
                onClick={() => adjustPrepTime(1)} 
                className="p-2 bg-blue-100 rounded-full hover:bg-blue-200 transition-all transform hover:scale-110 disabled:opacity-50"
                disabled={gamePhase !== 'idle'}
              >
                <HandDrawnButton type="plus" />
              </button>
            </div>
          </div>
          
          <div className="bg-white p-3 rounded-lg shadow-sm border border-blue-100">
            <h3 className="font-semibold mb-2 text-blue-800">Practice Time</h3>
            <div className="flex items-center justify-between">
              <button 
                onClick={() => adjustPracticeTime(-1)} 
                className="p-2 bg-blue-100 rounded-full hover:bg-blue-200 transition-all transform hover:scale-110 disabled:opacity-50"
                disabled={gamePhase !== 'idle'}
              >
                <HandDrawnButton type="minus" />
              </button>
              <span className="text-2xl font-bold text-blue-600">{practiceTime}s</span>
              <button 
                onClick={() => adjustPracticeTime(1)} 
                className="p-2 bg-blue-100 rounded-full hover:bg-blue-200 transition-all transform hover:scale-110 disabled:opacity-50"
                disabled={gamePhase !== 'idle'}
              >
                <HandDrawnButton type="plus" />
              </button>
            </div>
          </div>
          
          {/* Yellow note tip */}
          <div className="mt-8 p-4 bg-yellow-100 rounded-lg border-2 border-yellow-200 shadow-md transform rotate-1" style={{ backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 20px, rgba(255,255,255,0.3) 22px)' }}>
            <div className="text-lg font-bold text-red-600 mb-2">Explore your topic from the following aspects:</div>
            <ul className="text-red-500 pl-6 space-y-1">
              <li>Who?</li>
              <li>When?</li>
              <li>Where?</li>
              <li>Why?</li>
              <li>Have you ever?</li>
            </ul>
          </div>
        </div>
        
        {/* Right side board area */}
        <div className="game-board-area">
          <div className="grid grid-cols-4 gap-3 mb-6">
            {currentTopics.map((topic, index) => (
              <div
                key={index}
                className={`p-4 border-2 ${index === position ? 'bg-red-500 text-white' : 'bg-white'} 
                          rounded-lg shadow text-center relative min-h-[100px] flex items-center justify-center
                          transition-all duration-300 hover:shadow-md
                          ${bombPositions.includes(index) ? 'border-yellow-300' : 'border-blue-200'}
                          ${index === currentTopics.length - 1 ? 'border-green-500 border-2' : ''}`}
              >
                <span className="text-md font-medium">{topic}</span>
                {bombPositions.includes(index) && 
                  <span className="absolute top-1 right-1 text-yellow-500 animate-pulse">💣</span>
                }
                {index === currentTopics.length - 1 && 
                  <span className="absolute top-1 right-1 text-green-500">🏁</span>
                }
              </div>
            ))}
          </div>

          {/* 重新设计的状态显示框 */}
          <div className="bg-blue-50 rounded-lg shadow border border-blue-100 p-4 mb-6">
            <div className="flex justify-between items-center">
              <div className="text-xl font-semibold text-blue-700">
                {t('current_phase')}
                <span className="text-red-600 inline-block animate-pulse">
                  {phaseText}
                </span>
                {gamePhase === 'prep' && (
                  <span className="ml-2 text-blue-600">
                    {t('listen_and_prepare')}
                  </span>
                )}
              </div>
              
              {/* 骰子图标 */}
              <div className={`mx-4 ${diceRolling ? 'animate-spin' : 'hover:scale-110 transition-transform'}`}
                   style={{ 
                     minWidth: '64px', 
                     height: '64px',
                     display: 'flex', 
                     justifyContent: 'center', 
                     alignItems: 'center',
                     padding: '4px'
                   }}>
                {diceIcons[diceValue - 1]}
              </div>
              
              <div className={`font-bold transition-all duration-300 ${
                (gamePhase === 'practice' || gamePhase === 'prep' || gamePhase === 'listening')
                  ? 'text-4xl text-red-600' 
                  : 'text-2xl text-blue-700'
              }`}>
                {t('countdown')}{timer}{t('seconds')}
              </div>
            </div>
          </div>

          {/* 录音状态指示器 - 优化为类似语音消息的样式 */}
          {gamePhase === 'practice' && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              padding: '8px 15px',
              backgroundColor: '#90cdf4', // 改为浅蓝色
              color: 'white',
              borderRadius: '18px',
              width: '180px',
              margin: '15px auto',
              justifyContent: 'space-between'
            }}>
              <div style={{
                display: 'flex', 
                alignItems: 'center'
              }}>
                <div style={{
                  width: '12px',
                  height: '12px',
                  borderRadius: '50%',
                  backgroundColor: '#ff0000',
                  marginRight: '10px',
                  animation: 'pulse 1.5s infinite'
                }}></div>
                <span style={{ whiteSpace: 'nowrap' }}>{t('recording_voice')}</span>
              </div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                height: '20px',
                marginLeft: '10px'
              }}>
                <div style={{
                  width: '3px',
                  height: '8px',
                  margin: '0 3px',
                  backgroundColor: '#ff0000', // 改为红色
                  borderRadius: '2px',
                  animation: 'wave 1.5s infinite ease-in-out'
                }}></div>
                <div style={{
                  width: '3px',
                  height: '14px',
                  margin: '0 3px',
                  backgroundColor: '#ff0000', // 改为红色
                  borderRadius: '2px',
                  animation: 'wave 1.5s infinite ease-in-out 0.2s'
                }}></div>
                <div style={{
                  width: '3px',
                  height: '10px',
                  margin: '0 3px',
                  backgroundColor: '#ff0000', // 改为红色
                  borderRadius: '2px',
                  animation: 'wave 1.5s infinite ease-in-out 0.4s'
                }}></div>
                <div style={{
                  width: '3px',
                  height: '12px',
                  margin: '0 3px',
                  backgroundColor: '#ff0000', // 改为红色
                  borderRadius: '2px',
                  animation: 'wave 1.5s infinite ease-in-out 0.6s'
                }}></div>
              </div>
            </div>
          )}

          {/* 按钮区域 */}
          <div className="text-center mb-6 p-4 bg-white rounded-lg shadow-md border border-blue-100">
            <div className="flex justify-center space-x-6">
              {/* 抽签按钮，始终显示 */}
              <button
                onClick={rollDice}
                disabled={gamePhase !== 'idle'}
                className={`px-8 py-4 rounded-lg shadow-lg whitespace-nowrap ${
                  gamePhase === 'idle' 
                    ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white hover:from-blue-600 hover:to-purple-700' 
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                <span>
                  {t('rollDice')}
                </span>
              </button>
              <button
                onClick={updateVocab}
                disabled={gamePhase !== 'idle'}
                className={`px-8 py-4 font-bold text-white whitespace-nowrap ${gamePhase === 'idle' ? 'bg-purple-500 hover:bg-purple-700' : 'bg-gray-400 cursor-not-allowed'} rounded-full transition-all duration-300 transform hover:scale-105 shadow-md flex items-center justify-center`}
              >
                <RefreshCw className="mr-2" size={20} />
                {t('update_vocabulary')}
              </button>
              <button
                onClick={resetGame}
                className={`px-8 py-4 font-bold text-white whitespace-nowrap ${
                  reachedEnd 
                    ? 'bg-green-500 hover:bg-green-700' 
                    : 'bg-gray-400 cursor-not-allowed'
                } rounded-full transition-all duration-300 transform hover:scale-105 shadow-md flex items-center justify-center`}
                disabled={!reachedEnd}
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                </svg>
                {t('reset_game')}
              </button>
            </div>
          </div>

          {/* Special task display (when bomb is hit) */}
          {bombTask && (
            <div className="mb-6 p-4 bg-red-100 border-l-4 border-red-500 rounded-md">
              <h3 className="text-xl font-bold text-red-700 mb-2">{t('bombTask')}</h3>
              <p className="text-red-700">{bombTask}</p>
            </div>
          )}

          {/* 修改居中弹窗提示，只在到达终点时显示 */}
          {reachedEnd && showTranscriptionOptions && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white p-8 rounded-xl max-w-md w-full shadow-2xl border-4 border-green-500 relative">
                {/* 添加关闭按钮 */}
                <button 
                  onClick={() => {
                    // 只关闭弹窗，不改变reachedEnd状态
                    setShowTranscriptionOptions(false);
                  }} 
                  className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 cursor-pointer z-50"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                </button>
                
                <div className="text-center">
                  <div className="text-green-500 text-6xl mb-4">🏁</div>
                  <h2 className="text-3xl font-bold mb-4 text-red-600">{t('finish_congratulation')}</h2>
                  <p className="text-gray-600 mb-8">{t('part1_question_count')}</p>
                  
                  {/* 添加记录保存时间的提示 */}
                  <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200 mb-6">
                    <p className="text-yellow-700 font-medium">{t('records_save_notice')}</p>
                  </div>
                  
                  <div className="space-y-4">
                    <button
                      onClick={exportHistory}
                      className="w-full py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center justify-center"
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path>
                      </svg>
                      {t('export_conversation')}
                    </button>
                    
                    <button
                      onClick={viewScoreReport}
                      className="w-full py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 flex items-center justify-center"
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
                      </svg>
                      {t('end_training')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Scene selector dropdown menu */}
      {showSceneSelector && (
        <div className="scene-dropdown fixed z-50 w-48 bg-white rounded-md shadow-lg py-1 border border-gray-200" 
             style={{ 
               top: `${dropdownPosition.top}px`, 
               left: `${dropdownPosition.left}px`,
               width: `${dropdownPosition.width}px`
             }}>
          {/* Filter exam types for current language only */}
          {Object.keys(languageSettings)
            .filter(examType => languageSettings[examType].name === currentLanguageSetting.name)
            .map((type) => (
            <button
              key={type}
              className={`block w-full text-left px-4 py-2 text-sm hover:bg-blue-50 ${initialScene === type ? 'bg-blue-100 font-medium' : ''}`}
              onClick={() => changeExamType(type)}
            >
              <span className="flex items-center justify-between">
                {type}
                {initialScene === type && <ChevronDown className="transform rotate-180" size={16} />}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Game Rules Dialog */}
      {showRules && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-8 rounded-xl max-w-4xl max-h-[80vh] overflow-y-auto relative">
            <h2 className="text-2xl font-bold mb-4">{t('gameRules')}</h2>
            <button 
              onClick={() => setShowRules(false)} 
              className="mt-6 px-6 py-3 bg-blue-500 text-white font-semibold rounded-lg hover:bg-blue-600"
            >
              {t('closeRules')}
            </button>
          </div>
        </div>
      )}

      {/* 提分报告模态框 */}
      {showScoreReport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-8 rounded-xl max-w-md w-full shadow-2xl border-4 border-purple-500 relative">
            {/* 添加关闭按钮 */}
            <button 
              onClick={() => setShowScoreReport(false)} 
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 cursor-pointer z-50"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
            
            <div className="text-center">
              <div className="text-purple-500 text-6xl mb-4">📊</div>
              <h2 className="text-2xl font-bold mb-4 text-purple-600">{t('ielts_improvement_report')}</h2>
              <p className="text-gray-600 mb-8">{t('feature_optimizing')}</p>
              
              <div className="bg-purple-50 p-4 rounded-lg mb-6">
                <p className="text-purple-700">{t('report_optimization_message')}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default TalktasticVoiceGame;
